import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '../../chat/api'
import { FeatherIcon } from '../../components/FeatherIcon'
import { LoadingBlock, Spinner } from '../../components/Spinner'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { Sentinel } from '../../library/Sentinel'
import { useToast } from '../../library/useToast'
import { useCrmRequest } from '../CrmRequest'
import { StatsCards } from './StatsSection'
import { WithdrawModal } from './WithdrawModal'
import {
  appendPage,
  CHARGEBACK_PERIODS,
  chargebackRange,
  submitWithdrawal,
  dateParts,
  descriptionParts,
  descriptionType,
  emptyList,
  gatewayError,
  intlLocale,
  listRequest,
  minPayout,
  money,
  newKey,
  periodHintKey,
  plainText,
  referralUrl,
  SECTIONS,
  shortDate,
  statsCards,
  timezoneLabel,
  trimNumber,
  unwrapGateway,
  userFacingError,
  type Balances,
  type ListSection,
  type ListState,
  type Raw,
  type Receive,
  type Section,
  type StatsCard,
  type WithdrawOutcome,
} from './statementsData'
import '../../library/library.css'
import '../ofPages.css'
import './statements.css'

interface Props {
  accountId: string
  canWrite: boolean
  /** The person may open Banking (account owners and super admins). */
  canBank: boolean
  /** Navigates to another CRM page of this account ('banking', 'statistics', …). */
  onOpenPage: (page: string) => void
  /** Opens the fan's chat on the Chats page (fan names in descriptions). */
  onOpenChat: (fanId: number | string) => void
  mobileNav?: ReactNode
}

/** Everything OnlyFans answered, for the page's lifetime (tabs switch instantly). */
interface State {
  /** Bumps on Reload: answers to an older generation are dropped. */
  gen: number
  balances: Balances | null
  receive: Receive | null
  balancesLoading: boolean
  /** Null: no error; '' an error without a message. */
  balancesError: string | null
  periodSaving: boolean
  lists: Record<ListSection, ListState>
  chargebackDays: number
  /** Null while loading, NaN when it failed. */
  ratio: number | null
  /** The chargeback list (its id) the ratio belongs to. */
  ratioFor: number
  referral: { status: 'idle' | 'loading' | 'done'; earnings: number; ofUserId: string }
  stats: { cards: StatsCard[] | null; loading: boolean; loaded: boolean; error: string | null }
}

const freshLists = (): Record<ListSection, ListState> => ({
  earnings: emptyList(),
  payouts: emptyList(),
  chargebacks: emptyList(),
  referrals: emptyList(),
})

const initialState = (gen = 0, chargebackDays = 30): State => ({
  gen,
  balances: null,
  receive: null,
  balancesLoading: false,
  balancesError: null,
  periodSaving: false,
  lists: freshLists(),
  chargebackDays,
  ratio: null,
  ratioFor: 0,
  referral: { status: 'idle', earnings: NaN, ofUserId: '' },
  stats: { cards: null, loading: false, loaded: false, error: null },
})

const isObj = (v: unknown): v is Raw => Boolean(v) && typeof v === 'object' && !Array.isArray(v)
const messageOf = (e: unknown) => (e instanceof Error ? e.message : '')

/** Statements: balances, payout requests and earnings of the account (onlyfans.com/my/statements). */
export function StatementsPage({ accountId, canWrite, canBank, onOpenPage, onOpenChat, mobileNav }: Props) {
  const { t, i18n } = useTranslation('ofStatements')
  const request = useCrmRequest()
  const mobile = useIsNarrow()
  const { say, node: toast } = useToast(4000)
  const locale = intlLocale(i18n.language)

  const [section, setSection] = useState<Section>('earnings')
  const [collapsed, setCollapsed] = useState(false)
  const [statsOpen, setStatsOpen] = useState<ReadonlySet<string>>(() => new Set(['all']))
  const [withdrawing, setWithdrawing] = useState(false)
  const scroll = useRef<HTMLDivElement | null>(null)

  // The state, plus a synchronous copy the loaders read (cursor, in-flight flags).
  const [state, setState] = useState<State>(() => initialState())
  const live = useRef(state)
  const update = useCallback((fn: (s: State) => State) => {
    live.current = fn(live.current)
    setState(live.current)
  }, [])
  const setList = useCallback(
    (key: ListSection, id: number, fn: (l: ListState) => Partial<ListState>) =>
      update((s) => (s.lists[key].id === id ? { ...s, lists: { ...s.lists, [key]: { ...s.lists[key], ...fn(s.lists[key]) } } } : s)),
    [update],
  )

  /** One OnlyFans call through the CRM gateway; throws its readable error (CRM apiPostWithError). */
  const call = useCallback(
    async <T,>(path: string, body: Raw = {}): Promise<T> => {
      const { status, data } = await request<unknown>(path, { body: { account_id: accountId, ...body } })
      const failed = gatewayError(data, '')
      if (failed !== null) throw new Error(failed)
      if (status >= 400) throw new Error(userFacingError(errorMessage(data, ''), ''))
      return unwrapGateway<T>(data)
    },
    [request, accountId],
  )

  // ── Balances ─────────────────────────────────────────────────────────────
  const loadBalances = useCallback(async () => {
    const { gen, balancesLoading } = live.current
    if (balancesLoading) return
    update((s) => ({ ...s, balancesLoading: true, balancesError: null }))
    const [balances, receive] = await Promise.allSettled([call<Balances>('/v1/payouts/balances'), call<Receive>('/v1/payouts/check-receive')])
    if (live.current.gen !== gen) return
    update((s) => ({
      ...s,
      balancesLoading: false,
      balances: balances.status === 'fulfilled' && isObj(balances.value) ? balances.value : s.balances,
      balancesError: balances.status === 'fulfilled' && isObj(balances.value) ? null : balances.status === 'rejected' ? messageOf(balances.reason) : '',
      receive: receive.status === 'fulfilled' && isObj(receive.value) ? receive.value : null,
    }))
  }, [call, update])

  const changePeriod = async (code: string) => {
    const { balances, periodSaving, gen } = live.current
    if (!canWrite || !balances || !code || code === balances.withdrawalPeriod || periodSaving) return
    const previous = balances.withdrawalPeriod
    const setPeriod = (p: string | undefined) => (s: State) => ({ ...s, balances: s.balances && { ...s.balances, withdrawalPeriod: p } })
    update((s) => ({ ...setPeriod(code)(s), periodSaving: true }))
    try {
      await call('/v1/payouts/requests/period', { withdrawal_period: code, idempotency_key: newKey('period') })
      if (live.current.gen === gen) say(t('balance.periodUpdated'))
    } catch (e) {
      if (live.current.gen === gen) {
        update(setPeriod(previous))
        say(t('balance.periodFailed', { error: messageOf(e) || t('requestFailed') }))
      }
    } finally {
      update((s) => ({ ...s, periodSaving: false }))
    }
  }

  // ── Paged lists ──────────────────────────────────────────────────────────
  const loadRatio = useCallback(
    async (listId: number, days: number) => {
      update((s) => ({ ...s, ratio: null, ratioFor: listId }))
      let ratio = NaN
      try {
        const body = await call<Raw>('/v1/payouts/chargebacks/ratio', chargebackRange(days))
        ratio = Number(body?.chargebacksRatio)
      } catch {
        // Shown as "—".
      }
      update((s) => (s.ratioFor === listId && s.lists.chargebacks.id === listId ? { ...s, ratio } : s))
    },
    [call, update],
  )

  const loadPage = useCallback(
    async (key: ListSection) => {
      const s = live.current
      const list = s.lists[key]
      if (list.loading || !list.hasMore) return
      const { id } = list
      const req = listRequest(key, list.cursor, s.chargebackDays)
      setList(key, id, () => ({ loading: true, error: null }))
      if (key === 'chargebacks' && s.ratioFor !== id && list.cursor === null) void loadRatio(id, s.chargebackDays)
      try {
        const body = await call<unknown>(req.path, req.body)
        setList(key, id, (l) => ({ ...appendPage(key, l, body), loading: false, loaded: true }))
      } catch (e) {
        setList(key, id, () => ({ error: messageOf(e), loading: false, loaded: true }))
      }
    },
    [call, loadRatio, setList],
  )

  const resetList = (key: ListSection, patch: Partial<State> = {}) =>
    update((s) => ({ ...s, ...patch, lists: { ...s.lists, [key]: emptyList() } }))

  const setChargebackDays = (days: number) => {
    resetList('chargebacks', { chargebackDays: days })
    void loadPage('chargebacks')
  }

  // ── Referrals (the side card) and earning statistics ─────────────────────
  const loadReferralSide = useCallback(async () => {
    const { gen, referral } = live.current
    if (referral.status !== 'idle') return
    update((s) => ({ ...s, referral: { ...s.referral, status: 'loading' } }))
    const [balance, me] = await Promise.allSettled([call<Raw>('/v1/referrals/balance'), call<Raw>('/v1/users/me')])
    if (live.current.gen !== gen) return
    update((s) => ({
      ...s,
      referral: {
        status: 'done',
        earnings: balance.status === 'fulfilled' ? Number(balance.value?.referralEarnings) || 0 : NaN,
        ofUserId: me.status === 'fulfilled' && me.value?.id ? String(me.value.id) : '',
      },
    }))
  }, [call, update])

  const loadStats = useCallback(async () => {
    const { gen, stats } = live.current
    if (stats.loading || stats.loaded) return
    update((s) => ({ ...s, stats: { ...s.stats, loading: true, error: null } }))
    let next: State['stats']
    try {
      const body = await call<Raw>('/v1/payouts/stats')
      next = { cards: statsCards(isObj(body?.list) ? body.list : null), loading: false, loaded: true, error: null }
    } catch (e) {
      next = { cards: null, loading: false, loaded: true, error: messageOf(e) }
    }
    if (live.current.gen === gen) update((s) => ({ ...s, stats: next }))
  }, [call, update])

  const retryStats = () => {
    update((s) => ({ ...s, stats: { cards: null, loading: false, loaded: false, error: null } }))
    void loadStats()
  }

  // ── What a section needs (each loads once; answers stay for the page's lifetime) ──
  const ensure = useCallback(
    (which: Section) => {
      const s = live.current
      if (which === 'stats') void loadStats()
      else {
        if (which === 'referrals') void loadReferralSide()
        if (!s.lists[which].loaded) void loadPage(which)
      }
    },
    [loadPage, loadReferralSide, loadStats],
  )

  // Balances and the open section together, at once (and again after Reload).
  useEffect(() => {
    void loadBalances()
  }, [loadBalances, state.gen])
  useEffect(() => {
    ensure(section)
  }, [ensure, section, state.gen])

  const goSection = (next: Section) => {
    if (scroll.current) scroll.current.scrollTop = 0
    setSection(next)
  }

  const reload = () => update((s) => initialState(s.gen + 1, s.chargebackDays))

  // ── Withdrawal ───────────────────────────────────────────────────────────
  const submit = useCallback((amount: number, key: string) => submitWithdrawal(request, accountId, amount, key), [request, accountId])

  const withdrawalFinished = (outcome: WithdrawOutcome) => {
    setWithdrawing(false)
    if (outcome.kind === 'ok') {
      say(
        outcome.state === 'approved'
          ? t('payout.processed')
          : outcome.state === 'rejected'
            ? [t('payout.returned'), outcome.rejectReason && t('payout.reason', { reason: outcome.rejectReason })].filter(Boolean).join(' ')
            : t('payout.processing'),
      )
    } else say(t('withdraw.unknown'))
    resetList('payouts')
    void loadBalances()
    goSection('payouts')
    void loadPage('payouts')
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const { balances: b, balancesLoading, balancesError, lists, referral, stats } = state
  const currency = b?.currency || 'USD'
  const balanceLoading = balancesLoading && !b
  const placeholder = balanceLoading ? '…' : '—'
  const minLabel = money(minPayout(b), currency, 0)
  const period = String(b?.withdrawalPeriod ?? '')
  const options = Array.isArray(b?.withdrawalPeriodOptions) ? b.withdrawalPeriodOptions : []
  const pendingDays = Number(b?.manualPayoutPendingDays)
  const pendingTip = Number.isFinite(pendingDays) && pendingDays > 0 ? t('balance.pendingTip', { n: pendingDays }) : t('balance.pendingTipDefault')
  const hint = balancesError !== null ? balancesError || t('balance.loadFailed') : b ? t(`balance.${periodHintKey(period)}`, { min: minLabel }) : ''
  const cannotReceive = state.receive?.canReceiveManualPayout === false
  const belowMin = (Number(b?.payoutAvailable) || 0) < minPayout(b)
  const requestTitle = cannotReceive ? t('balance.cannotReceive') : belowMin ? t('balance.belowMin', { min: minLabel }) : undefined

  const openChat = (fanId: string) => onOpenChat(fanId)

  const statusIcon = (icon: string, label: string, tone: string) => (
    <span className={`ofs-status-icon is-${tone}`} role="img" tabIndex={0} title={label} aria-label={label}>
      <FeatherIcon name={icon} size={18} />
    </span>
  )

  const dateCell = (value: unknown, oneLine = false) => {
    const d = dateParts(value, locale)
    if (!d) return '—'
    return oneLine ? (
      `${d.day} ${d.time}`
    ) : (
      <>
        {d.day}
        <br />
        <span>{d.time}</span>
      </>
    )
  }

  const description = (row: Raw) => {
    const parts = descriptionParts(row)
    if (!parts) return descriptionType(row) || t('operation')
    return parts.map((p, i) =>
      p.fanId ? (
        <button key={i} type="button" className="ofs-fan-link" onClick={() => openChat(p.fanId!)}>
          {p.text}
        </button>
      ) : (
        <span key={i}>{p.text}</span>
      ),
    )
  }

  const earningStatus = (row: Raw) => {
    const status = String(row.status ?? '').toLowerCase()
    if (status === 'loading') {
      const days = Number(row.payoutPendingDays)
      return statusIcon('clock', Number.isFinite(days) && days > 0 ? t('status.pendingDays', { n: days }) : t('status.pending'), 'pending')
    }
    if (status === 'done') return statusIcon('check-circle', t('status.complete'), 'done')
    if (status === 'undo') {
      const reason = plainText(row.refundReason)
      return statusIcon('undo-2', reason ? t('status.reversedReason', { reason }) : t('status.reversed'), 'reversed')
    }
    return status ? <span className="ofs-status-text">{status}</span> : null
  }

  const payoutState = (row: Raw) => {
    const st = String(row.state ?? '').toLowerCase()
    if (st === 'approved') {
      return (
        <>
          <span className="ofs-state">{t('payout.processed')}</span>
          {statusIcon('check-circle', t('status.processed'), 'done')}
        </>
      )
    }
    if (st === 'rejected') {
      const reason = plainText(row.rejectReason)
      return (
        <>
          <span className="ofs-state is-rejected">
            {t('payout.returned')}
            {reason && <span className="ofs-state-reason">{t('payout.reason', { reason })}</span>}
          </span>
          {statusIcon('alert-circle', t('status.rejected'), 'rejected')}
        </>
      )
    }
    if (st === 'new' || st === 'pending') {
      return (
        <>
          <span className="ofs-state">{t('payout.processing')}</span>
          {statusIcon('clock', t('status.processing'), 'pending')}
        </>
      )
    }
    return <span className="ofs-state">{st || '—'}</span>
  }

  const loadingCard = (
    <div className="ofs-card">
      <div className="ofs-empty">
        <LoadingBlock label={t('loading')} />
      </div>
    </div>
  )
  const firstLoad = (l: ListState) => !l.loaded && l.rows.length === 0 && l.error === null

  const footer = (key: ListSection, l: ListState) => {
    if (l.error !== null) {
      return (
        <div className="ofs-footer is-error">
          <span>{l.error || t('requestFailed')}</span>
          <button type="button" className="ofs-link-btn" onClick={() => void loadPage(key)}>
            {t('retry')}
          </button>
        </div>
      )
    }
    if (l.loading) {
      return (
        <div className="ofs-footer">
          <Spinner size={20} label={t('loading')} />
        </div>
      )
    }
    if (l.hasMore) return <Sentinel onVisible={() => void loadPage(key)} token={l.rows.length} />
    return null
  }
  const emptyRow = (l: ListState, text: string) => (l.rows.length || l.loading || l.error !== null ? null : <div className="ofs-empty">{text}</div>)

  const earnings = () => {
    const l = lists.earnings
    if (firstLoad(l)) return loadingCard
    return (
      <div className="ofs-card ofs-card--table">
        <table className="ofs-table ofs-table--earnings">
          <thead>
            <tr>
              <th>{t('col.dateTime')}</th>
              <th className="ofs-num">{t('col.amount')}</th>
              <th className="ofs-num">{t('col.fee')}</th>
              <th className="ofs-num">{t('col.net')}</th>
              <th>{t('col.description')}</th>
              <th className="ofs-status">{t('col.status')}</th>
            </tr>
          </thead>
          <tbody>
            {l.rows.map((row, i) => {
              const cur = String(row.currency || 'USD')
              return (
                <tr key={String(row.id ?? i)}>
                  <td className="ofs-date">{dateCell(row.createdAt)}</td>
                  <td className="ofs-num">{money(row.amount, cur)}</td>
                  <td className="ofs-num">{money(row.fee, cur)}</td>
                  <td className="ofs-num">{money(row.net, cur)}</td>
                  <td className="ofs-desc">{description(row)}</td>
                  <td className="ofs-status">{earningStatus(row)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {emptyRow(l, t('empty.earnings'))}
        {footer('earnings', l)}
      </div>
    )
  }

  const payoutTable = (key: 'payouts' | 'referrals', emptyText: string) => {
    const l = lists[key]
    if (firstLoad(l)) return loadingCard
    return (
      <div className="ofs-card ofs-card--table">
        <table className="ofs-table ofs-table--payouts">
          <thead>
            <tr>
              <th>{t('col.dateTime')}</th>
              <th>{t('col.invoice')}</th>
              <th className="ofs-num">{t('col.amount')}</th>
              <th>{t('col.status')}</th>
            </tr>
          </thead>
          <tbody>
            {l.rows.map((row, i) => (
              <tr key={String(row.id ?? i)}>
                <td className="ofs-date">{dateCell(row.createdAt, true)}</td>
                <td className="ofs-mono">{String(row.invoiceId || '—')}</td>
                <td className="ofs-num is-strong">{money(row.amount, String(row.currency || 'USD'))}</td>
                <td>
                  <div className="ofs-state-cell">{payoutState(row)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {emptyRow(l, emptyText)}
        {footer(key, l)}
      </div>
    )
  }

  const chargebacks = () => {
    const l = lists.chargebacks
    const range = chargebackRange(state.chargebackDays)
    const ratio = state.ratio === null ? '…' : Number.isFinite(state.ratio) ? `${trimNumber(state.ratio)}%` : '—'
    const head = (
      <div className="ofs-chargeback-head">
        <label className="ofs-period">
          <select aria-label={t('chargebacks.period')} value={state.chargebackDays} onChange={(e) => setChargebackDays(Number(e.target.value) || 30)}>
            {CHARGEBACK_PERIODS.map((days) => (
              <option key={days} value={days}>
                {t('chargebacks.days', { count: days })}
              </option>
            ))}
          </select>
          <small>
            {shortDate(range.start_date, locale)} – {shortDate(range.end_date, locale)}
          </small>
        </label>
        <div className="ofs-ratio">
          <FeatherIcon name="info" size={18} />
          <span>
            {t('chargebacks.ratio')} <strong>{ratio}</strong>
          </span>
        </div>
      </div>
    )
    if (firstLoad(l)) {
      return (
        <>
          {head}
          {loadingCard}
        </>
      )
    }
    const total = l.rows.reduce((sum, row) => sum + (Number((row.payment as Raw | undefined)?.amount) || 0), 0)
    return (
      <>
        {head}
        <div className="ofs-card ofs-card--table">
          <div className="ofs-disputes">
            {l.rows.length > 0 && (
              <div className="ofs-dispute-total">
                <span>{t('chargebacks.total')}</span>
                <strong>{money(total)}</strong>
              </div>
            )}
            <div className="ofs-dispute-grid ofs-dispute-header">
              <div>{t('col.disputeDate')}</div>
              <div>{t('col.paymentDate')}</div>
              <div className="ofs-num">{t('col.amount')}</div>
              <div className="ofs-num">{t('col.feeNet')}</div>
            </div>
            {l.rows.map((row, i) => {
              const p = isObj(row.payment) ? row.payment : {}
              const cur = String(p.currency || 'USD')
              return (
                <div className="ofs-dispute" key={String(row.id ?? i)}>
                  <div className="ofs-dispute-grid">
                    <div className="ofs-date is-strong">{dateCell(row.createdAt)}</div>
                    <div className="ofs-date">{dateCell(p.createdAt)}</div>
                    <div className="ofs-num">{money(p.amount, cur)}</div>
                    <div className="ofs-num">
                      {money(p.fee, cur)}
                      <br />
                      <strong>{money(p.net, cur)}</strong>
                    </div>
                  </div>
                  <div className="ofs-dispute-desc">
                    <span className="ofs-desc">{description(p)}</span>
                    {statusIcon('undo-2', t('status.chargedBack'), 'reversed')}
                  </div>
                </div>
              )
            })}
          </div>
          {emptyRow(l, t('empty.chargebacks'))}
          {footer('chargebacks', l)}
        </div>
      </>
    )
  }

  const statsSection = () => {
    if (!stats.loaded && stats.error === null) return loadingCard
    if (stats.error !== null) {
      return (
        <div className="ofs-card">
          <div className="ofs-footer is-error">
            <span>{stats.error || t('requestFailed')}</span>
            <button type="button" className="ofs-link-btn" onClick={retryStats}>
              {t('retry')}
            </button>
          </div>
        </div>
      )
    }
    if (!stats.cards) {
      return (
        <div className="ofs-card">
          <div className="ofs-empty">{t('empty.stats')}</div>
        </div>
      )
    }
    return (
      <StatsCards
        cards={stats.cards}
        currency={currency}
        locale={locale}
        open={statsOpen}
        onToggle={(id) =>
          setStatsOpen((all) => {
            const next = new Set(all)
            if (!next.delete(id)) next.add(id)
            return next
          })
        }
      />
    )
  }

  const sectionBody = () => {
    if (!accountId) {
      return (
        <div className="ofs-card">
          <div className="ofs-empty">{t('selectAccount')}</div>
        </div>
      )
    }
    switch (section) {
      case 'earnings':
        return earnings()
      case 'payouts':
        return (
          <>
            <div className="ofs-info">
              <FeatherIcon name="info" size={18} />
              <span>{t('payout.info')}</span>
            </div>
            {payoutTable('payouts', t('empty.payouts'))}
          </>
        )
      case 'stats':
        return statsSection()
      case 'chargebacks':
        return chargebacks()
      case 'referrals':
        return payoutTable('referrals', t('empty.referrals'))
    }
  }

  const refUrl = referralUrl(referral.ofUserId)
  const copyReferral = async () => {
    if (!refUrl) return
    try {
      await navigator.clipboard.writeText(refUrl)
      say(t('referral.copied'))
    } catch {
      say(refUrl)
    }
  }

  const referralCard = section === 'referrals' && (
    <div className="ofs-side-card ofs-referral" aria-label={t('referral.earnings')} role="group">
      <div className="ofs-referral-head">
        <span>{t('referral.earnings')}</span>
        <strong>{referral.status !== 'done' ? '…' : Number.isNaN(referral.earnings) ? '—' : money(referral.earnings, currency)}</strong>
      </div>
      <p className="ofs-referral-note">{t('referral.note', { min: minLabel })}</p>
      <div className="ofs-referral-url">
        <div className="ofs-referral-url-top">
          <span>{t('referral.url')}</span>
          <button type="button" className="ofs-link-btn" disabled={!refUrl} onClick={() => void copyReferral()}>
            {t('referral.copy')}
          </button>
        </div>
        <div className="ofs-referral-url-value">{refUrl || (referral.status !== 'done' ? '…' : '—')}</div>
      </div>
    </div>
  )

  const balanceCard = (
    <div className={`ofs-side-card ofs-balance ${collapsed ? 'is-collapsed' : ''}`}>
      <button type="button" className="ofs-balance-head" aria-expanded={!collapsed} onClick={() => setCollapsed((c) => !c)}>
        <span>{t('balance.current')}</span>
        <strong data-testid="ofs-current-balance">{b ? money(b.payoutAvailable, currency) : placeholder}</strong>
        <FeatherIcon name="chevron-up" size={16} className="ofs-balance-chevron" />
      </button>
      {!collapsed && (
        <div className="ofs-balance-body">
          <div className="ofs-pending-row">
            <span>
              {t('balance.pending')}
              <span className="ofs-info-tip" tabIndex={0} title={pendingTip} aria-label={pendingTip}>
                <FeatherIcon name="info" size={15} />
              </span>
            </span>
            <strong data-testid="ofs-pending-balance">{b ? money(b.payoutPending, currency) : placeholder}</strong>
          </div>
          <div className="ofs-payout-box">
            <select
              className="ofs-period-select"
              aria-label={t('balance.frequency')}
              value={options.length ? period : ''}
              disabled={!options.length || state.periodSaving || !canWrite}
              onChange={(e) => void changePeriod(e.target.value)}
            >
              {options.length ? (
                options.map((o) => (
                  <option key={o.code} value={o.code}>
                    {t(`periodCode.${o.code}`, { defaultValue: o.name || o.code })}
                  </option>
                ))
              ) : (
                <option value="">{balanceLoading ? '…' : '—'}</option>
              )}
            </select>
            <div className={`ofs-minimum ${balancesError !== null ? 'is-error' : ''}`}>{hint}</div>
            {canWrite && period === 'manual' && (
              <button
                type="button"
                className="ofs-request-btn"
                disabled={!b || cannotReceive || belowMin}
                title={requestTitle}
                onClick={() => setWithdrawing(true)}
              >
                {t('balance.request')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )

  const menuIcon: Record<Section, string> = {
    earnings: 'dollar-sign',
    payouts: 'hand-coins',
    stats: 'bar-chart-2',
    chargebacks: 'undo-2',
    referrals: 'users',
  }
  const menu = (
    <nav className="ofs-menu" aria-label={t('menuLabel')}>
      {SECTIONS.map(({ id }) => (
        <button
          key={id}
          type="button"
          className={section === id ? 'is-on' : ''}
          aria-current={section === id ? 'page' : undefined}
          onClick={() => goSection(id)}
        >
          <FeatherIcon name={menuIcon[id]} size={19} />
          <span>{t(`section.${id}`)}</span>
        </button>
      ))}
      {canBank && (
        <button type="button" className="ofs-menu-banking" onClick={() => onOpenPage('banking')}>
          <FeatherIcon name="credit-card" size={19} />
          <span>{t('banking')}</span>
          <FeatherIcon name="chevron-right" size={16} className="ofs-menu-go" />
        </button>
      )}
    </nav>
  )

  const meta = SECTIONS.find((s) => s.id === section)!
  const main = (
    <>
      <div className="ofs-section-top">
        <h2>{t(`sectionTitle.${section}`)}</h2>
        <span>{meta.time === 'utc' ? t('utcTime') : t('localTime', { tz: timezoneLabel() })}</span>
      </div>
      <div className="ofs-section">{sectionBody()}</div>
    </>
  )

  const head = (
    <header className="lib-main-head ofs-head">
      <button type="button" className="lib-icon-btn" aria-label={t('back')} title={t('back')} onClick={() => onOpenPage('statistics')}>
        <FeatherIcon name="arrow-left" size={19} />
      </button>
      <h1 className="lib-main-title ofs-title">{t('title')}</h1>
      <span className="lib-spacer" />
      <button type="button" className="lib-icon-btn" aria-label={t('reload')} title={t('reload')} onClick={reload}>
        <FeatherIcon name="rotate-cw" size={18} />
      </button>
    </header>
  )

  const modal = withdrawing && canWrite && b && (
    <WithdrawModal balances={b} submit={submit} onFinished={withdrawalFinished} onClose={() => setWithdrawing(false)} />
  )

  if (mobile) {
    return (
      <div className="lib lib--m ofp ofs ofs--m">
        {head}
        <div className="ofs-scroll" ref={scroll}>
          <div className="ofs-side">
            {referralCard}
            {balanceCard}
            {menu}
          </div>
          <div className="ofs-body">{main}</div>
        </div>
        {mobileNav}
        {modal}
        {toast}
      </div>
    )
  }

  return (
    <div className="lib ofp ofs">
      <aside className="ofs-side">
        {referralCard}
        {balanceCard}
        {menu}
      </aside>
      <main className="lib-main ofs-main">
        {head}
        <div className="ofs-scroll" ref={scroll}>
          <div className="ofs-body">{main}</div>
        </div>
      </main>
      {modal}
      {toast}
    </div>
  )
}

